import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Search, AlertCircle, Calendar, Wallet, FileWarning } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { LeaseCard } from "@/components/leases/LeaseCard";
import { AgreementModal } from "@/components/leases/AgreementModal";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

const StatCard = ({ label, value, icon, valueClass = "text-white", testId }) => (
  <Card className="card-dashboard">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm">{label}</p>
          <p className={`text-2xl font-bold ${valueClass}`} data-testid={testId}>{value}</p>
        </div>
        {icon}
      </div>
    </CardContent>
  </Card>
);

const LeasesPage = () => {
  const { token } = useAuth();
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [togglingId, setTogglingId] = useState(null);
  const [agreementLease, setAgreementLease] = useState(null);
  const [savingAgreement, setSavingAgreement] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchLeases = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leases`, { headers: { Authorization: `Bearer ${token}` } });
      setLeases(response.data);
    } catch (error) {
      toast.error("Failed to load leases");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLeases();
  }, [fetchLeases]);

  const handleTogglePayment = async (lease, status) => {
    setTogglingId(lease.lease_id);
    try {
      await axios.post(`${API}/leases/${lease.lease_id}/payment-status`, { status }, authHeaders);
      toast.success(status === "paid" ? `Rent marked as paid for ${lease.tenant_name}` : `Rent reset to pending for ${lease.tenant_name}`);
      await fetchLeases();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update payment status");
    } finally {
      setTogglingId(null);
    }
  };

  const handleSaveAgreement = async (lease, file, terms) => {
    setSavingAgreement(true);
    try {
      const formData = new FormData();
      if (file) formData.append("file", file);
      formData.append("terms", terms);
      await axios.post(`${API}/leases/${lease.lease_id}/agreement`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      toast.success("Lease agreement saved");
      setAgreementLease(null);
      await fetchLeases();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save agreement");
    } finally {
      setSavingAgreement(false);
    }
  };

  const handleDownloadAgreement = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API}/files/${fileId}/download`, { ...authHeaders, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || "agreement");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download agreement");
    }
  };

  const filteredLeases = leases.filter((lease) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      lease.tenant_name?.toLowerCase().includes(q) ||
      lease.tenant_email?.toLowerCase().includes(q) ||
      lease.asset_name?.toLowerCase().includes(q);
    const matchesStatus =
      filterStatus === "all" ||
      lease.status === filterStatus ||
      (filterStatus === "expiring" && (lease.alert === "30_days" || lease.alert === "90_days")) ||
      (filterStatus === "paid" && lease.payment_status === "paid") ||
      (filterStatus === "unpaid" && lease.payment_status !== "paid" && lease.alert !== "expired") ||
      (filterStatus === "no_agreement" && !lease.has_agreement);
    return matchesSearch && matchesStatus;
  });

  const activeLeases = leases.filter((l) => l.status === "active");
  const currentLeases = activeLeases.filter((l) => l.alert !== "expired");
  const collected = currentLeases.filter((l) => l.payment_status === "paid").reduce((s, l) => s + (l.rent_amount || 0), 0);
  const outstanding = currentLeases.filter((l) => l.payment_status !== "paid").reduce((s, l) => s + (l.rent_amount || 0), 0);
  const missingAgreements = leases.filter((l) => !l.has_agreement).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="leases-page">
        <div>
          <h1 className="text-3xl font-bold text-white">Leases</h1>
          <p className="text-slate-400 mt-1">Manage all your property leases, rent collection and agreements</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search by tenant, email or property..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 input-default"
              data-testid="search-leases-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-52 input-default" data-testid="filter-lease-status-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leases</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="expiring">Expiring Soon</SelectItem>
              <SelectItem value="paid">Rent Paid</SelectItem>
              <SelectItem value="unpaid">Rent Pending</SelectItem>
              <SelectItem value="no_agreement">Missing Agreement</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatCard label="Total Leases" value={leases.length} icon={<Users className="w-8 h-8 text-primary" />} testId="stat-total-leases" />
          <StatCard
            label="Active"
            value={activeLeases.length}
            valueClass="text-emerald-400"
            icon={<div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center"><div className="w-3 h-3 rounded-full bg-emerald-500"></div></div>}
            testId="stat-active-leases"
          />
          <StatCard
            label="Expiring Soon"
            value={leases.filter((l) => l.alert === "30_days" || l.alert === "90_days").length}
            valueClass="text-amber-400"
            icon={<AlertCircle className="w-8 h-8 text-amber-500" />}
            testId="stat-expiring-leases"
          />
          <StatCard
            label="Expired"
            value={leases.filter((l) => l.alert === "expired").length}
            valueClass="text-red-400"
            icon={<Calendar className="w-8 h-8 text-red-500" />}
            testId="stat-expired-leases"
          />
          <StatCard
            label="Collected (this period)"
            value={formatCurrency(collected)}
            valueClass="text-emerald-400 mono text-xl"
            icon={<Wallet className="w-8 h-8 text-emerald-500" />}
            testId="stat-rent-collected"
          />
          <StatCard
            label="Outstanding"
            value={formatCurrency(outstanding)}
            valueClass="text-amber-400 mono text-xl"
            icon={<Wallet className="w-8 h-8 text-amber-500" />}
            testId="stat-rent-outstanding"
          />
        </div>

        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span>All Leases</span>
              {missingAgreements > 0 && (
                <span className="text-sm font-normal text-red-400 flex items-center gap-1" data-testid="missing-agreements-count">
                  <FileWarning className="w-4 h-4" /> {missingAgreements} without agreement
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredLeases.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">{searchTerm ? "No leases found matching your search" : "No leases recorded yet"}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredLeases.map((lease) => (
                  <LeaseCard
                    key={lease.lease_id}
                    lease={lease}
                    toggling={togglingId === lease.lease_id}
                    onTogglePayment={handleTogglePayment}
                    onUploadAgreement={setAgreementLease}
                    onDownloadAgreement={handleDownloadAgreement}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <AgreementModal
          lease={agreementLease}
          open={!!agreementLease}
          onOpenChange={(open) => !open && setAgreementLease(null)}
          onSubmit={handleSaveAgreement}
          saving={savingAgreement}
        />
      </div>
    </DashboardLayout>
  );
};

export default LeasesPage;
